import { Divider } from '@mui/material';
import { filter, includes, map } from 'lodash';
import moment from 'moment';
import React, { Fragment, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  FormControls,
  FormikArray,
  FormikDateTimePicker,
  FormikNumericField,
  FormikSelect,
} from 'components';
import { OrderType } from 'enums';
import { useUserContextUtils } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { AmountAntiFraudRule, TradeMethod } from 'types';

type Props = {
  orderType: OrderType;
  tradeMethods?: TradeMethod[];
};

export const AmountAntiFraud: React.FC<Props> = ({
  orderType,
  tradeMethods,
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.shops.manage',
  });
  const { t: tCommon } = useTranslation();

  const now = useMemo(() => moment(), []);
  const name = useMemo(() => `${orderType}AmountAntiFraudRules`, [orderType]);

  const { paymentTypesOptions: allPaymentTypesOptions } = useUserContextUtils();
  const paymentTypesOptions = useMemo(() => {
    const paymentTypeIds = map(
      tradeMethods,
      (tradeMethod) => tradeMethod.paymentTypeId,
    );
    return filter(allPaymentTypesOptions, (option) =>
      includes(paymentTypeIds, option.value),
    );
  }, [allPaymentTypesOptions, tradeMethods]);

  const renderItemComponent = useCallback(
    ({ item, index }: { item: AmountAntiFraudRule; index: number }) => {
      const helperText = item.expiredAt
        ? now.isAfter(item.expiredAt) && (
            <span className="tw-text-red-500">
              {t('sections.amount_anti_fraud.expired_rule')}
            </span>
          )
        : t('sections.amount_anti_fraud.permanent_rule');

      return (
        <FormControls sx={{ flex: 1 }}>
          {item.system && (
            <div className="tw-text-xs tw-italic tw-text-gray-500">
              {t('sections.amount_anti_fraud.auto_generated_rule')}
            </div>
          )}
          <FormControls row>
            <FormikNumericField
              label={t('sections.amount_anti_fraud.fields.min_amount')}
              name={`${name}[${index}].minAmount`}
              fullWidth
              allowNegative={false}
              disabled={item.system}
            />
            <FormikNumericField
              label={t('sections.amount_anti_fraud.fields.max_amount')}
              name={`${name}[${index}].maxAmount`}
              fullWidth
              allowNegative={false}
              disabled={item.system}
            />
          </FormControls>
          <FormikSelect
            name={`${name}[${index}].paymentTypeId`}
            label={t('sections.amount_anti_fraud.fields.payment_type')}
            options={paymentTypesOptions}
            fullWidth
            noneOption
            disabled={item.system}
          />
          <FormikDateTimePicker
            label={t('sections.amount_anti_fraud.fields.expired_at')}
            name={`${name}[${index}].expiredAt`}
            clearable
            variant="outlined"
            size="small"
            disabled={item.system}
            helperText={helperText}
          />
        </FormControls>
      );
    },
    [t, name, paymentTypesOptions, now],
  );

  const isItemHidden = useCallback(
    (item: AmountAntiFraudRule) => !!item.system,
    [],
  );

  return (
    <Fragment>
      <Divider>
        {t('sections.amount_anti_fraud.title', {
          orderType: tCommon(`common.${orderType}`),
        })}
      </Divider>

      <FormikArray
        addItemLabel={t('buttons.add_rule')}
        name={name}
        removable
        defaultValue={{
          minAmount: 0,
          maxAmount: '',
          paymentTypeId: '',
          system: false,
        }}
        ItemComponent={renderItemComponent}
        isItemHidden={isItemHidden}
      />
    </Fragment>
  );
};
